// src/pages/admin/members/AdminRegionalReps.tsx
//
// Regional Reps management — admin-only view.
//
// ACCESS:
//   admin only — full view and edit (managed region, basic profile)
//   all others — tab is hidden from the router; this page shows an access wall
//
// ACTIONS CAPTURED:
//   GET   /admin/users?role=regional_rep   — load all reps
//   PATCH /admin/users/:id                 — update region, province, managedRegion, ministryRole
//   GET   /tasks/submissions/pending       — grading queue count per rep's region
//                                            (read from pending submissions already cached)

import React, { useState, useMemo } from "react";
import {
  UserCog,
  MapPin,
  Clock,
  Mail,
  Pencil,
  RefreshCw,
  Search,
  Shield,
} from "lucide-react";

import {
  CAN_VIEW_REPS,
  Bone,
  fmtDate,
  Avatar,
  EditMemberPanel,
  StatTile,
} from "./MemberShared";
import { ManagedUser, useGetUsersQuery } from "../../app/api/adminApi";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/api/authApi";
import { useGetPendingSubmissionsQuery } from "../../app/api/tasksApi";

/* ─────────────────────────────────────────────────────────────────
   REP ROW
───────────────────────────────────────────────────────────────── */
const RepRow: React.FC<{
  rep: ManagedUser;
  pendingCount: number;
  onEdit: (r: ManagedUser) => void;
}> = ({ rep, pendingCount, onEdit }) => (
  <div
    className={`flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors ${!rep.isActive ? "opacity-50" : ""}`}
  >
    <Avatar user={rep} size="md" colour="bg-amber-100 text-amber-700" />

    {/* Name + email */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {rep.firstName} {rep.lastName}
        </p>
        {!rep.isActive && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 shrink-0">
            Inactive
          </span>
        )}
        {!rep.isEmailVerified && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 shrink-0">
            Unverified
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
        <Mail size={9} />
        {rep.email}
      </p>
    </div>

    {/* Managed region */}
    <div className="hidden sm:block shrink-0 text-right min-w-0 max-w-[140px]">
      <p className="text-xs font-semibold text-amber-700 truncate flex items-center justify-end gap-1">
        <MapPin size={10} />
        {rep.managedRegion ?? "Not set"}
      </p>
      <p className="text-[10px] text-gray-400">Managed region</p>
    </div>

    {/* Ministry role */}
    <div className="hidden md:block shrink-0 text-right max-w-[130px]">
      <p className="text-[11px] text-gray-600 truncate">
        {rep.ministryRole ?? "—"}
      </p>
      <p className="text-[10px] text-gray-400">Ministry role</p>
    </div>

    {/* Pending submissions badge */}
    <div className="hidden lg:block shrink-0 text-right w-20">
      {pendingCount > 0 ? (
        <div>
          <p className="text-sm font-bold text-red-600">{pendingCount}</p>
          <p className="text-[10px] text-gray-400">pending grades</p>
        </div>
      ) : (
        <p className="text-[11px] text-green-600 font-medium">All graded</p>
      )}
    </div>

    {/* Last login */}
    <div className="hidden xl:block shrink-0 text-right w-28">
      <p className="text-[11px] text-gray-500 flex items-center justify-end gap-1">
        <Clock size={9} />
        {fmtDate(rep.lastLoginAt)}
      </p>
      <p className="text-[10px] text-gray-400">Last login</p>
    </div>

    <button
      onClick={() => onEdit(rep)}
      title="Edit rep"
      className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
    >
      <Pencil size={14} />
    </button>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────────────── */
const AdminRegionalReps: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_chair") as UserRole;

  const canView = CAN_VIEW_REPS.includes(role);

  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState<ManagedUser | null>(null);

  /* Query — always load all reps (max 100, they're a small set) */
  const {
    data: repsRes,
    isLoading,
    isFetching,
    refetch,
  } = useGetUsersQuery(
    { role: "regional_rep", limit: 100 },
    { skip: !canView },
  );

  /* Pending submissions — used to show grading backlog per rep's region.
     We pull the full unfiltered pending list once; this is already cached
     if AdminTasks.tsx has been visited. */
  const { data: pendingRes } = useGetPendingSubmissionsQuery(
    {},
    { skip: !canView },
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reps = repsRes?.data ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pending = pendingRes?.data ?? [];

  /* Build a map of managedRegion → pending count */
  const pendingByRegion = useMemo(() => {
    const map: Record<string, number> = {};
    pending.forEach((sub) => {
      const region = (sub.mentee as any)?.region ?? "";
      if (region) map[region] = (map[region] ?? 0) + 1;
    });
    return map;
  }, [pending]);

  /* Client-side search (rep list is tiny, no server round-trip needed) */
  const filtered = useMemo(() => {
    if (!search.trim()) return reps;
    const q = search.toLowerCase();
    return reps.filter(
      (r) =>
        r.firstName.toLowerCase().includes(q) ||
        r.lastName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        (r.managedRegion?.toLowerCase().includes(q) ?? false),
    );
  }, [reps, search]);

  /* Aggregate stats */
  const stats = useMemo(() => {
    const active = reps.filter((r) => r.isActive).length;
    const noRegion = reps.filter((r) => !r.managedRegion).length;
    const totalPend = pending.length;
    return { total: reps.length, active, noRegion, totalPend };
  }, [reps, pending]);

  /* Access gate */
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <Shield size={28} className="text-gray-300" />
        <p className="text-sm font-semibold text-gray-600">
          Admin access required
        </p>
        <p className="text-xs text-gray-400 max-w-xs">
          Regional Reps management is visible to Super Admins only.
        </p>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5 pb-8">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Total reps"
          value={stats.total}
          colour="text-amber-700"
          icon={<UserCog size={12} className="text-amber-400" />}
          loading={isLoading}
        />
        <StatTile
          label="Active"
          value={stats.active}
          colour="text-green-700"
          icon={<span className="w-2 h-2 rounded-full bg-green-400" />}
        />
        <StatTile
          label="No region set"
          value={stats.noRegion}
          colour={stats.noRegion > 0 ? "text-red-600" : "text-gray-400"}
          icon={<MapPin size={12} className="text-red-400" />}
        />
        <StatTile
          label="Pending grades"
          value={stats.totalPend}
          colour={stats.totalPend > 0 ? "text-red-600" : "text-gray-400"}
          icon={<span className="w-2 h-2 rounded-full bg-red-400" />}
        />
      </div>

      {/* Search + refresh */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by name, email, or region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
          />
        </div>
        <button
          onClick={refetch}
          disabled={isFetching}
          className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw
            size={14}
            className={
              isFetching ? "animate-spin text-gray-400" : "text-gray-500"
            }
          />
        </button>
      </div>

      {/* Rep list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-700">
            {isLoading
              ? "Loading…"
              : `${filtered.length} rep${filtered.length !== 1 ? "s" : ""}`}
          </p>
        </div>

        {/* Column headers — visible on wider screens */}
        <div className="hidden xl:grid grid-cols-[1fr_140px_130px_80px_112px_36px] gap-4 px-5 py-2 border-b border-gray-50">
          {[
            "Rep",
            "Managed Region",
            "Ministry Role",
            "Grading",
            "Last Login",
            "",
          ].map((h) => (
            <p
              key={h}
              className="text-[10px] font-bold uppercase tracking-wide text-gray-400"
            >
              {h}
            </p>
          ))}
        </div>

        {isLoading ? (
          <div className="p-4 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Bone key={i} className="h-16" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
            <UserCog size={24} className="text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">
              No Regional Reps found
            </p>
            <p className="text-xs text-gray-400">
              {search
                ? "Try adjusting the search."
                : "Invite reps from the Admin Team page."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((rep) => {
              // Count pending submissions for the region this rep manages
              const regionKey = rep.managedRegion ?? "";
              const repPending = regionKey
                ? (pendingByRegion[regionKey] ?? 0)
                : 0;
              return (
                <RepRow
                  key={rep.id}
                  rep={rep}
                  pendingCount={repPending}
                  onEdit={setEditTarget}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Reps without a managed region — warning table */}
      {stats.noRegion > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
          <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
            <MapPin size={12} /> {stats.noRegion} rep
            {stats.noRegion !== 1 ? "s" : ""} with no managed region
          </p>
          <p className="text-[11px] text-amber-700">
            Reps without a managed region cannot grade submissions or nominate
            members. Edit their profile to assign one.
          </p>
        </div>
      )}

      {/* Edit panel */}
      {editTarget && (
        <EditMemberPanel
          member={editTarget}
          callerRole={role}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminRegionalReps;
